import {
  ensure,
  oneOf,
  asAString,
  asAInteger,
  wrapErrors,
  itsProperty,
  ensureProperties,
  equalTo,
  anyOf,
  beBetween,
  validateNotOverlapingOf,
} from "../Common/composition";
import IpV4V6 from "common/ip";
import AddressInput from "../Access/Address";
import ProtocolInput, { protocols } from "../Access/Protocol";
import PortInput from "../Access/Port";

const invalid = (error) => {
  throw error;
};

const validIpV4V6CidrOrAddr = (input) =>
  IpV4V6.isValidCidrOrAddr(input)
    ? IpV4V6.normalizeCidr(input)
    : invalid("Invalid address or range");

const asteriskMeansAny = (input) => (input === "*" ? "any" : input);

const itsName = ({ name }) => name;

const validateProtocol = wrapErrors(
  (error) => `Protocol ${error}`,
  ensure(
    asteriskMeansAny,
    oneOf(
      asAString(anyOf("any", ...protocols.map(itsName))),
      asAInteger(beBetween(0, 255))
    )
  )
);

const validatePort = wrapErrors(
  (error) => `Port ${error}`,
  ensure(
    asteriskMeansAny,
    oneOf(asAString(equalTo("any")), asAInteger(beBetween(1, 65535)))
  )
);
const describe = ({ address, protocol, port }) =>
  'entry ' +
  address +
  (protocol === "any" ? "" : ` on ${protocol} protocol`) +
  (port === "any" ? "" : ` on ${port} port`);

const compare = (one, another) =>
  one.address === another.address &&
  String(one.protocol) === String(another.protocol) &&
  String(one.port) === String(another.port);

const validateEntry = wrapErrors(
  (error) => `Entry ${error}`,
  ensure(
    ensureProperties(
      itsProperty("address", validIpV4V6CidrOrAddr),
      itsProperty("protocol", validateProtocol),
      itsProperty("port", validatePort)
    ),
    validateNotOverlapingOf(compare, describe)
  )
);

const protocolAndPort = (protocol, port = "any") => `${protocol} port ${port}`;

const protocolNumber = (protocol) =>
  (protocols.find(({ name }) => name === protocol) || {}).number || protocol;

const layer4Protocol = (protocol) => `l4-protocol ${protocolNumber(protocol)}`;

const assertAddressAndPortWithProtocol = ({ address, protocol, port }) => [
  `address ${address}`,
  ...(protocol === "any"
    ? []
    : port !== "any" && (protocol === "tcp" || protocol === "udp")
    ? [protocolAndPort(protocol, port)]
    : [layer4Protocol(protocol)]),
  "exit",
];

const assertExistingEntries = ({ added = [], keeped = [], updated = [] }) => [
  ...[...added, ...keeped, ...updated].map(assertAddressAndPortWithProtocol),
];

const parseAddressAndProtocol = line => {
  const [address, protocol='any', port='any'] = line.trim().split(/\s+/);
  return {address, protocol: protocol || 'any' , port: port || 'any'};
}

const accessComposition = {
  cliType: "access",
  typeLabel: "Access",
  viewURL: "viewAccessProfileConfig",
  expectClear: true,
  redeclareToUpdate: true,
  fields: [
    { name: "name", label: "Profile name", type: "text", canUpdate: true },
    {
      name: "entries",
      cliName: "entry",
      label: "entries",
      labelSingle: "entry",
      type: "many",
      importable: true,
      importLineParse: parseAddressAndProtocol,
      canBeEmpty: false,
      cliExpressAs: assertExistingEntries /* profile is cleared before */,
      fields: [
        {
          name: "address",
          label: "Address",
          type: "text",
          icon: "cloud_queue",
          InputComponent: AddressInput,
          validate: validIpV4V6CidrOrAddr,
        },
        {
          name: "protocol",
          label: "Protocol",
          type: "text",
          icon: "device_hub",
          InputComponent: ProtocolInput,
          validate: validateProtocol,
          parse: asteriskMeansAny,
        },
        {
          name: "port",
          label: "Port",
          type: "number",
          icon: "settings_input_hdmi",
          InputComponent: PortInput,
          validate: validatePort,
          parse: asteriskMeansAny,
        },
      ],
      validate: validateEntry,
    },
  ],
};

export default accessComposition;
